import React, { useEffect, useState } from "react";
import {
  LuCheckCircle as CheckCircle,
  LuXCircle as XCircle,
  LuInfo as Info,
  LuAlertTriangle as AlertTriangle,
} from "react-icons/lu";

type ToastProps = {
  message: string;
  type?: "success" | "error" | "info" | "warning";
};

const getToastColorByType = (type: ToastProps["type"] = "info") => {
  const styles = {
    success:
      "bg-gradient-to-r from-green-50/95 to-emerald-50/95 border-green-200/60 text-green-800 shadow-green-500/25",
    error: "bg-gradient-to-r from-red-50/95 to-rose-50/95 border-red-200/60 text-red-800 shadow-red-500/25",
    warning: "bg-gradient-to-r from-amber-50/95 to-yellow-50/95 border-amber-200/60 text-amber-800 shadow-amber-500/25",
    info: "bg-gradient-to-r from-blue-50/95 to-indigo-50/95 border-blue-200/60 text-blue-800 shadow-blue-500/25",
  }[type];

  return styles;
};

const getToastIcon = (type: ToastProps["type"] = "info") => {
  const iconClass = "w-5 h-5 flex-shrink-0";

  switch (type) {
    case "success":
      return <CheckCircle className={`${iconClass} text-green-600`} />;
    case "error":
      return <XCircle className={`${iconClass} text-red-600`} />;
    case "warning":
      return <AlertTriangle className={`${iconClass} text-amber-600`} />;
    case "info":
    default:
      return <Info className={`${iconClass} text-blue-600`} />;
  }
};

export const Toast: React.FC<ToastProps> = ({ message, type = "info" }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const colorStyles = getToastColorByType(type);
  const icon = getToastIcon(type);

  return (
    <div
      className={`
        ${colorStyles}
        min-w-80 max-w-md p-5 rounded-2xl border backdrop-blur-md
        shadow-xl shadow-black/10
        transform transition-all duration-500 ease-out
        ${isVisible ? "translate-x-0 opacity-100 scale-100" : "translate-x-8 opacity-0 scale-95"}
        hover:scale-105 hover:shadow-2xl hover:-translate-y-1
        relative overflow-hidden
        group cursor-pointer
      `}
    >
      {/* Animated background shine effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 animate-shimmer" />
      </div>

      {/* Subtle border glow */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-white/30 via-transparent to-white/30 opacity-60" />

      {/* Content */}
      <div className="relative flex items-center gap-4">
        {/* Icon with subtle pulse animation */}
        <div className="animate-pulse-subtle">{icon}</div>

        {/* Message */}
        <div className="flex-1">
          <p className="text-sm font-semibold leading-relaxed tracking-wide">{message}</p>
        </div>
      </div>

      {/* Bottom progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10 rounded-b-2xl overflow-hidden">
        <div
          className={`h-full rounded-b-2xl animate-shrink ${
            type === "success"
              ? "bg-green-500"
              : type === "error"
              ? "bg-red-500"
              : type === "warning"
              ? "bg-amber-500"
              : "bg-blue-500"
          }`}
          style={{ animationDuration: "3500ms" }}
        />
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }
        
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        .animate-shrink {
          animation: shrink linear forwards;
        }
        
        .animate-pulse-subtle {
          animation: pulse-subtle 2s infinite;
        }
      `}</style>
    </div>
  );
};
